/**
 * Offline persistence telemetry
 * =============================
 * One canonical emitter for every offline persistence action, so the first
 * failing stage of an offline session can be identified from telemetry alone
 * — without another production investigation.
 *
 * Every event carries: app version, git commit, build number, job id,
 * appraisal id, offline/online state and a timestamp. Events are buffered by
 * the observability client and persisted locally when there is no
 * connectivity, so an entire no-signal working day is replayed on reconnect.
 *
 * Fire-and-forget: never throws, never blocks, never changes behaviour.
 */

import { captureEvent } from "@/lib/observability";
import { getReleaseInfo } from "@/lib/releaseInfo";
import { isKnownOffline } from "@/lib/connectivity";

/**
 * Diagnostic-only error descriptor for telemetry. Keeps `error` as the
 * original message when available; null/empty reasons (e.g. an aborted
 * IndexedDB transaction whose `tx.error` is null) get an explicit fallback
 * instead of the literal string "null". Never includes payloads.
 */
export function describeTelemetryError(error: unknown): {
  error: string;
  error_name: string | null;
  error_reason_type: string;
  error_stack: string | null;
} {
  const reasonType = error === null ? "null" : typeof error;
  if (error instanceof Error || (error && typeof error === "object")) {
    const e = error as { name?: unknown; message?: unknown; stack?: unknown };
    const message = typeof e.message === "string" && e.message ? e.message : "";
    const name = typeof e.name === "string" && e.name ? e.name : null;
    return {
      error: (message || (name ? `${name} (no message)` : "error object without message")).slice(0, 300),
      error_name: name,
      error_reason_type: error instanceof Error ? "error" : reasonType,
      error_stack: typeof e.stack === "string" && e.stack ? e.stack.slice(0, 1500) : null,
    };
  }
  if (error === null || error === undefined || error === "") {
    return {
      error: `no error reason provided (${error === "" ? "empty string" : String(error)})`,
      error_name: null,
      error_reason_type: error === "" ? "empty_string" : reasonType,
      error_stack: null,
    };
  }
  return { error: String(error).slice(0, 300), error_name: null, error_reason_type: reasonType, error_stack: null };
}

export type OfflineTelemetryEvent =
  // Draft lifecycle
  | "draft_save_started"
  | "draft_save_completed"
  | "draft_save_failed"
  | "draft_restore_started"
  | "draft_restore_completed"
  | "draft_restore_failed"
  // Native (Capacitor Preferences) backup
  | "native_backup_written"
  | "native_backup_write_failed"
  | "native_backup_restored"
  | "native_backup_missing"
  | "native_backup_read_failed"
  // IndexedDB
  | "indexeddb_write_success"
  | "indexeddb_write_failure"
  // Photos
  | "photo_write_success"
  | "photo_write_failure"
  // Durable photo persistence (raw-byte store, schema v2)
  | "durable_persist_success"
  | "durable_reopen_read_success"
  | "durable_read_failure"
  | "durable_read_timeout"
  // Photo upload pipeline
  | "upload_start"
  | "bytes_uploading"
  | "storage_response"
  | "storage_verification_success"
  | "storage_verification_deferred"

  | "upload_success"
  // Photo lifecycle observability (1.60.50 — reporting only, no behaviour)
  | "photo_db_pending"
  | "photo_attach_retry"
  | "photo_terminal_classified"
  | "photo_state_transition_failed"
  | "photo_patch_missing_record"
  // Byte integrity (1.60.51 — H3/H4)
  | "photo_integrity_hash_created"
  | "photo_integrity_length_mismatch"
  | "photo_integrity_hash_mismatch"
  // Storage authorisation (1.63) — permanent, actionable RLS/403 rejection
  | "photo_upload_authorization_denied"
  // Delete/replace lifecycle (1.63.x) — an in-flight upload for a photo the
  // driver deleted is discarded instead of being reconciled back into the
  // appraisal's photo references.
  | "photo_upload_discarded_after_delete"
  // Deferred server-side deletion cleanup (1.63.x stage 1.1) — a delete made
  // offline keeps its reference prune durably queued until connectivity
  // returns, so a stale reference can never block completion again.
  | "photo_reference_cleanup_deferred"
  | "photo_reference_cleanup_completed"
  // Sync queue
  | "queue_enqueue_success"
  | "queue_enqueue_failure"
  | "queue_replay_started"
  | "queue_replay_completed"
  // Dashboard
  | "dashboard_refresh_triggered";

const FAILURE_EVENTS = new Set<OfflineTelemetryEvent>([
  "draft_save_failed",
  "draft_restore_failed",
  "native_backup_write_failed",
  "native_backup_read_failed",
  "indexeddb_write_failure",
  "photo_write_failure",
  "durable_read_failure",
  "durable_read_timeout",
  "queue_enqueue_failure",
  "photo_state_transition_failed",
  "photo_integrity_length_mismatch",
  "photo_integrity_hash_mismatch",
  "photo_upload_authorization_denied",
]);

/**
 * Failures that the pipeline absorbs and retries on its own. They are still
 * recorded as errors (nothing is suppressed), but at "warn" severity so an
 * expected, self-healing condition cannot raise a CRITICAL health incident.
 */
const RECOVERABLE_FAILURE_EVENTS = new Set<OfflineTelemetryEvent>([
  "photo_state_transition_failed",
  "durable_read_timeout",
]);


export interface OfflineTelemetryContext {
  jobId?: string | null;
  appraisalId?: string | null;
  /** Logical store / operation the event refers to, e.g. "sync_queue". */
  store?: string;
  /** Action type, photo slot, draft key suffix, etc. */
  subject?: string;
  bytes?: number;
  count?: number;
  durationMs?: number;
  error?: unknown;
  [key: string]: unknown;
}

function offlineState(): { offline: boolean; state: "offline" | "online" } {
  try {
    const offline = isKnownOffline();
    return { offline, state: offline ? "offline" : "online" };
  } catch {
    return { offline: false, state: "online" };
  }
}

/** Record one offline-persistence event. Never throws. */
export function logOfflineEvent(
  event: OfflineTelemetryEvent,
  ctx: OfflineTelemetryContext = {},
): void {
  try {
    const release = getReleaseInfo();
    const { offline, state } = offlineState();
    const { error, jobId, appraisalId, durationMs, ...rest } = ctx;
    const isFailure = FAILURE_EVENTS.has(event);
    // Expected offline conditions and self-healing retries stay on the record
    // as errors, but at warn severity so they are not treated as incidents.
    const recoverable = isFailure && (RECOVERABLE_FAILURE_EVENTS.has(event) || offline);

    captureEvent({
      kind: isFailure ? "error" : "performance",
      severity: isFailure ? (recoverable ? "warn" : "error") : "info",

      component: "offline.persistence",
      message: event,
      duration_ms: typeof durationMs === "number" ? durationMs : undefined,
      priority: "high",
      metadata: {
        ...rest,
        event,
        job_id: jobId ?? null,
        appraisal_id: appraisalId ?? null,
        offline,
        network_state: state,
        timestamp: new Date().toISOString(),
        app_version: release.appVersion,
        build_number: release.buildNumber,
        git_commit: release.gitCommit,
        platform: release.platform,
        ...(error !== undefined ? describeTelemetryError(error) : {}),
      },
    });
  } catch {
    /* telemetry must never break offline work */
  }
}

/**
 * Wrap an offline persistence operation: emits a `*_started` event, then the
 * matching completed/failed event with its duration. The original result or
 * error is always passed through unchanged.
 */
export async function trackOfflineOperation<T>(
  events: {
    started: OfflineTelemetryEvent;
    completed: OfflineTelemetryEvent;
    failed: OfflineTelemetryEvent;
  },
  ctx: OfflineTelemetryContext,
  fn: () => Promise<T>,
): Promise<T> {
  const startedAt = Date.now();
  logOfflineEvent(events.started, ctx);
  try {
    const result = await fn();
    logOfflineEvent(events.completed, { ...ctx, durationMs: Date.now() - startedAt });
    return result;
  } catch (error) {
    logOfflineEvent(events.failed, { ...ctx, durationMs: Date.now() - startedAt, error });
    throw error;
  }
}
